import { useCallback, useEffect, useState } from 'react';
import type { PodcastAnalytics, PodcastEpisodeView } from '@almanac/contracts';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useApi } from '../../api/useApi';
import { formatLongDate, formatMonthDay } from '../../lib/format';
import { FEED_LABELS } from '../../lib/podcast';

type AnalyticsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; analytics: PodcastAnalytics };

/** Load separately from the library so measurement failures never block listening. */
export function usePodcastAnalytics() {
  const api = useApi();
  const [state, setState] = useState<AnalyticsState>({ status: 'loading' });
  const [now, setNow] = useState(Date.now);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loading = false;
    async function load() {
      if (loading) return;
      loading = true;
      let next: AnalyticsState;
      try {
        next = { status: 'loaded', analytics: await api.getPodcastAnalytics() };
      } catch {
        next = { status: 'error' };
      }
      loading = false;
      if (!cancelled) setState(next);
    }
    function refresh() {
      setNow(Date.now());
      void load();
    }
    void load();
    const clock = window.setInterval(() => setNow(Date.now()), 60_000);
    const poll = window.setInterval(refresh, 5 * 60_000);
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(clock);
      window.clearInterval(poll);
      window.removeEventListener('focus', refresh);
    };
  }, [api, attempt]);

  return { state, retry, now };
}

/** Owner/admin-scoped download estimates, with explicit coverage and missing-data states. */
export function DownloadAnalytics({
  result,
  episodes,
}: {
  result: ReturnType<typeof usePodcastAnalytics>;
  episodes: PodcastEpisodeView[];
}) {
  const { state, retry, now } = result;
  return (
    <section aria-labelledby="podcast-downloads" className="mt-8">
      <Card className="gap-4 px-4 sm:px-5">
        <h2 id="podcast-downloads" className="font-display text-lg font-semibold">
          Estimated downloads
        </h2>
        {state.status === 'loading' ? (
          <output className="block text-muted-foreground">Loading download statistics…</output>
        ) : null}
        {state.status === 'error' ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-muted-foreground">Download statistics are unavailable right now.</p>
            <Button variant="outline" size="sm" onClick={retry}>
              Retry statistics
            </Button>
          </div>
        ) : null}
        {state.status === 'loaded' && state.analytics.status === 'pending' ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-muted-foreground">
              Download tracking is waiting for its first collection. Counts are not available yet.
            </p>
            <Button variant="outline" size="sm" onClick={retry}>
              Retry statistics
            </Button>
          </div>
        ) : null}
        {state.status === 'loaded' && state.analytics.status === 'ready' ? (
          <DownloadSummary analytics={state.analytics} episodes={episodes} now={now} />
        ) : null}
      </Card>
    </section>
  );
}

function DownloadSummary({
  analytics,
  episodes,
  now,
}: {
  analytics: Extract<PodcastAnalytics, { status: 'ready' }>;
  episodes: PodcastEpisodeView[];
  now: number;
}) {
  const titles = new Map(
    episodes.map((episode) => [`${episode.feed}:${episode.guid}`, episode.title]),
  );
  const total = analytics.episodes.reduce((sum, episode) => sum + episode.estimated_downloads, 0);
  const stale = now - new Date(analytics.data_through).getTime() > 3 * 60 * 60 * 1_000;
  const daily = new Map<string, number>();
  for (const episode of analytics.episodes) {
    for (const day of episode.daily)
      daily.set(day.date, (daily.get(day.date) ?? 0) + day.downloads);
  }
  const days = [...daily].sort(([left], [right]) => left.localeCompare(right));

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <p className="text-3xl font-semibold tabular-nums">
            {analytics.episodes.length === 0 ? '—' : total.toLocaleString()}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Across the episodes below, since{' '}
            {formatLongDate(analytics.coverage_started_at.slice(0, 10))} (UTC)
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          Data through{' '}
          <time dateTime={analytics.data_through}>
            {new Date(analytics.data_through).toLocaleString()}
          </time>
          {stale ? (
            <span className="mt-1 block font-medium text-danger">
              Update delayed — these counts may be incomplete.
            </span>
          ) : null}
        </p>
      </div>
      {analytics.episodes.length === 0 ? (
        <p className="text-muted-foreground">
          No episode statistics are available for your feeds yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">
              Estimated downloads per episode within the reported coverage
            </caption>
            <thead className="border-b border-border text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  Episode
                </th>
                <th scope="col" className="pb-2 pr-4 text-right font-medium">
                  Since coverage began
                </th>
                <th scope="col" className="pb-2 text-right font-medium">
                  First 7 days
                </th>
              </tr>
            </thead>
            <tbody>
              {analytics.episodes.map((episode) => (
                <tr
                  key={`${episode.feed}:${episode.guid}`}
                  className="border-b border-border last:border-0"
                >
                  <th scope="row" className="max-w-64 py-3 pr-4 text-left font-normal">
                    <span className="mb-0.5 block text-xs text-muted-foreground">
                      {FEED_LABELS[episode.feed]}
                    </span>
                    <span className="break-words">
                      {titles.get(`${episode.feed}:${episode.guid}`) ?? episode.guid}
                    </span>
                  </th>
                  <td className="py-3 pr-4 text-right font-medium tabular-nums">
                    {episode.estimated_downloads.toLocaleString()}
                  </td>
                  <td className="py-3 text-right tabular-nums">
                    {episode.first_7_days === null ? (
                      <span className="text-xs text-muted-foreground">Not fully covered</span>
                    ) : (
                      episode.first_7_days.toLocaleString()
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {days.length > 0 ? <DailyTrend days={days} /> : null}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground">
          How downloads are estimated
        </summary>
        <p className="mt-2 max-w-prose leading-relaxed">
          Includes automatic downloads and does not prove someone listened. We count a qualifying
          audio response once per episode and anonymous client per UTC calendar day. A response must
          contain about a minute of audio (or the full episode if shorter). Bots and probes are
          filtered. Downloads split into small transfers can be undercounted; overlapping transfers
          are never added together. First 7 days is shown only when that whole period is covered.
          Company statistics are available to admins; personal statistics are yours.
        </p>
      </details>
    </>
  );
}

/** A small daily bar chart with the same numbers available as an expandable text list. */
/* oxlint-disable jsx-a11y/prefer-tag-over-role -- Inline SVG needs an image role for its accessible label. */
function DailyTrend({ days }: { days: [string, number][] }) {
  const max = Math.max(1, ...days.map(([, count]) => count));
  return (
    <div>
      <p className="text-xs text-muted-foreground">Daily trend · last {days.length} days · UTC</p>
      <svg
        viewBox={`0 0 ${days.length * 12} 48`}
        className="mt-2 h-16 w-full text-primary"
        preserveAspectRatio="none"
        role="img"
        aria-label={`Daily estimated downloads, ${formatMonthDay(days[0]![0])} to ${formatMonthDay(days.at(-1)![0])}. Expand daily totals for the numbers.`}
      >
        <line x1="0" x2={days.length * 12} y1="47" y2="47" className="stroke-border" />
        {days.map(([date, count], index) => (
          <rect
            key={date}
            x={index * 12 + 1}
            y={47 - (count / max) * 44}
            width="10"
            height={(count / max) * 44}
            rx="1"
            fill="currentColor"
          >
            <title>
              {date}: {count} estimated downloads
            </title>
          </rect>
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        <span>{formatMonthDay(days[0]![0])}</span>
        <span>{formatMonthDay(days.at(-1)![0])}</span>
      </div>
      <details className="mt-2 text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground">Daily totals (UTC)</summary>
        <ol className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
          {days.map(([date, count]) => (
            <li key={date} className="flex justify-between gap-4">
              <time dateTime={date}>{date}</time>
              <span className="tabular-nums">{count.toLocaleString()}</span>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}
